import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Package, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_PRODUCTS = [
  { id: 'geobar-b', name: 'GEObar B', type: 'BFRP Rebar', description: '6" sample cut of #4 basalt fiber rebar' },
  { id: 'geobar-fg', name: 'GEObar FG', type: 'GFRP Rebar', description: '6" sample cut of #4 glass fiber rebar' },
  { id: 'geofabric', name: 'GEOfabric', type: 'Basalt Geogrid', description: '12"x12" basalt geogrid sample' },
  { id: 'chopped-fiber', name: 'GEOfiber', type: 'Chopped Fiber', description: '100g sample bag of chopped basalt fiber' },
];

export default function SampleRequestPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    samples: [],
    project_description: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSampleToggle = (sampleId) => {
    setFormData(prev => ({
      ...prev,
      samples: prev.samples.includes(sampleId)
        ? prev.samples.filter(id => id !== sampleId)
        : [...prev.samples, sampleId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.company.trim()) newErrors.company = 'Required';
    if (!formData.email.trim()) newErrors.email = 'Required';
    if (!formData.address.trim()) newErrors.address = 'Required';
    if (!formData.city.trim()) newErrors.city = 'Required';
    if (!formData.state.trim()) newErrors.state = 'Required';
    if (!formData.zip.trim()) newErrors.zip = 'Required';
    if (formData.samples.length === 0) newErrors.samples = 'Select at least one sample';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/sample-request`, formData);
      setSubmitSuccess(true);
      toast.success('Sample request submitted!');
    } catch (error) {
      toast.error('Error submitting request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="bg-charcoal min-h-screen pt-20" data-testid="sample-request-page">
        <section className="py-24 lg:py-32">
          <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="w-20 h-20 border border-gold/50 flex items-center justify-center mx-auto mb-8">
                <CheckCircle size={40} className="text-gold" />
              </div>
              <h1 className="heading-display text-stone text-4xl mb-6">SAMPLE REQUEST RECEIVED</h1>
              <p className="text-stone/60 text-lg mb-4">
                Your samples will ship within 2-3 business days.
              </p>
              <p className="text-stone/40 text-sm">
                A confirmation email has been sent to {formData.email}
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="sample-request-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-4">
              <Package size={24} className="text-gold" />
              <span className="tech-label">//REQUEST_SAMPLES</span>
            </div>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              Get <span className="text-gold">Samples</span>
            </h1>
            <p className="text-stone/50 max-w-2xl text-lg">
              Request free material samples to evaluate our composite reinforcement products. 
              Touch it, test it, specify it with confidence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Sample Selection */}
            <div>
              <h2 className="heading-section text-stone text-xl mb-6">Select Samples</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SAMPLE_PRODUCTS.map(sample => (
                  <button
                    key={sample.id}
                    type="button"
                    onClick={() => handleSampleToggle(sample.id)}
                    className={`p-6 text-left border transition-all ${
                      formData.samples.includes(sample.id)
                        ? 'border-gold bg-gold/10'
                        : 'border-border-dark hover:border-gold/30'
                    }`}
                    data-testid={`sample-${sample.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-mono text-xs text-gold/70">{sample.type}</span>
                        <h3 className="heading-section text-stone text-lg mt-1">{sample.name}</h3>
                        <p className="text-stone/50 text-sm mt-2">{sample.description}</p>
                      </div>
                      <div className={`w-5 h-5 border flex items-center justify-center ${
                        formData.samples.includes(sample.id) ? 'border-gold bg-gold' : 'border-stone/30'
                      }`}>
                        {formData.samples.includes(sample.id) && <CheckCircle size={14} className="text-charcoal" />}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {errors.samples && (
                <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.samples}
                </p>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="heading-section text-stone text-xl mb-6">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone text-sm mb-2">Name <span className="text-gold">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} className={`input ${errors.name ? 'border-red-500' : ''}`} />
                </div>
                <div>
                  <label className="block text-stone text-sm mb-2">Company <span className="text-gold">*</span></label>
                  <input type="text" name="company" value={formData.company} onChange={handleChange} className={`input ${errors.company ? 'border-red-500' : ''}`} />
                </div>
                <div>
                  <label className="block text-stone text-sm mb-2">Email <span className="text-gold">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className={`input ${errors.email ? 'border-red-500' : ''}`} />
                </div>
                <div>
                  <label className="block text-stone text-sm mb-2">Phone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input" />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div>
              <h2 className="heading-section text-stone text-xl mb-6">Shipping Address</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-stone text-sm mb-2">Street Address <span className="text-gold">*</span></label>
                  <input type="text" name="address" value={formData.address} onChange={handleChange} className={`input ${errors.address ? 'border-red-500' : ''}`} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="block text-stone text-sm mb-2">City <span className="text-gold">*</span></label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} className={`input ${errors.city ? 'border-red-500' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-stone text-sm mb-2">State <span className="text-gold">*</span></label>
                    <input type="text" name="state" value={formData.state} onChange={handleChange} className={`input ${errors.state ? 'border-red-500' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-stone text-sm mb-2">ZIP <span className="text-gold">*</span></label>
                    <input type="text" name="zip" value={formData.zip} onChange={handleChange} className={`input ${errors.zip ? 'border-red-500' : ''}`} />
                  </div>
                </div>
              </div>
            </div>

            {/* Project Info */}
            <div>
              <h2 className="heading-section text-stone text-xl mb-6">Project Information (Optional)</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-stone text-sm mb-2">Project Description</label>
                  <textarea name="project_description" value={formData.project_description} onChange={handleChange} rows={3} className="input resize-none" placeholder="Brief description of your project..." />
                </div>
                <div>
                  <label className="block text-stone text-sm mb-2">Estimated Timeline</label>
                  <input type="text" name="timeline" value={formData.timeline} onChange={handleChange} className="input" placeholder="e.g., Q2 2026" />
                </div>
              </div>
            </div>

            <button type="submit" disabled={isSubmitting} className="btn btn-primary w-full md:w-auto" data-testid="submit-sample-request">
              {isSubmitting ? 'Submitting...' : 'Request Samples'}
              <Send size={16} className="ml-2" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
